import { createFileRoute, redirect } from '@tanstack/react-router'
import { useI18n } from '@/i18n/i18n-react'
import { useGetLoyaltyCard } from '@/api/react-query'
import { LoyaltyCard } from '@/components/loyalty-card'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/loading-spinner'
import { ErrorDisplay } from '@/components/error-display'
import { useGetWalletUrl } from '@/hooks/wallet'
import { Wallet, ReceiptText } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { tenant } from '@/tenants/tenant'
import { Helmet } from 'react-helmet-async'
import { isApiError } from '@/api/api'

export const Route = createFileRoute('/loyalty')({
	component: LoyaltyPage,
	beforeLoad: () => {
		if (!tenant.features.loyaltyCardEnabled) {
			throw redirect({ to: '/receipts' })
		}
	}
})

function LoyaltyPage() {
	const { L } = useI18n()
	const { features } = tenant
	const loyaltyCard = useGetLoyaltyCard({ enabled: features.loyaltyCardEnabled })
	const walletUrl = useGetWalletUrl()

	if (loyaltyCard.error && isApiError(loyaltyCard.error) && loyaltyCard.error.httpStatusCode === 404) {
		return (
			<div className='container mx-auto max-w-lg p-6 text-center'>
				<p className='text-muted-foreground'>{L.LoyaltyCard.NoCard()}</p>
			</div>
		)
	}

	if (!features.loyaltyCardEnabled) {
		return (
			<div className='container mx-auto max-w-lg p-6 text-center'>
				<p className='text-muted-foreground'>{L.Errors.Generic()}</p>
			</div>
		)
	}

	if (loyaltyCard.isLoading) {
		return (
			<div className='container mx-auto flex max-w-lg items-center justify-center py-16'>
				<LoadingSpinner />
			</div>
		)
	}

	if (loyaltyCard.error) {
		return (
			<div className='container mx-auto max-w-lg p-6'>
				<ErrorDisplay refetch={loyaltyCard.refetch} />
			</div>
		)
	}

	return (
		<div className='container mx-auto max-w-lg px-4 py-8'>
			<Helmet>
				<title>{L.LoyaltyCard.Title()}</title>
			</Helmet>

			<h1 className='mb-6 text-2xl font-bold'>{L.LoyaltyCard.Title()}</h1>

			{loyaltyCard.data ?
				<div className='space-y-4'>
					<LoyaltyCard card={loyaltyCard.data} />

					<div className='flex flex-col gap-3'>
						{walletUrl?.url && (
							<Button
								className='w-full'
								onClick={() => window.open(walletUrl.url, '_blank', 'noopener,noreferrer')}
							>
								<Wallet className='mr-2 h-4 w-4' />
								{L.LoyaltyCard.AddToWallet()}
							</Button>
						)}

						<Button asChild variant='outline' className='w-full'>
							<Link to='/receipts'>
								<ReceiptText className='mr-2 h-4 w-4' />
								{L.LoyaltyCard.ShowReceipts()}
							</Link>
						</Button>
					</div>

					{loyaltyCard.data.walletLink === undefined && (
						<p className='text-muted-foreground text-center text-xs'>{L.LoyaltyCard.Disclaimer()}</p>
					)}
				</div>
			:	<div className='rounded-lg border p-8 text-center'>
					<p className='text-muted-foreground'>{L.LoyaltyCard.JoinProgram()}</p>
				</div>
			}
		</div>
	)
}
